<template>
  <div class="mobile-menu-container trap" id="mobile-menu-container">
    <button
      class="mobile-menu__open"
      type="button"
      v-show="false"
      @click="toggleMenu"
    />
    <transition name="slide-from-left">
      <div
        class="mobile-menu"
        v-show="isMenuActive"
        ref="menu"
        @touchend.passive="onTouchEnd"
        @touchstart.passive="onTouchStart"
      >
        <div
          class="mobile-menu__top"
          v-if="showTopBlock"
          ref="top"
          :inert="isPanelOpen"
        >
          <Search v-if="!noSearch" ref="search" @active="onSearchActive" />
        </div>

        <div class="mobile-menu__content" :inert="isSheetOpen">
          <NestedMenu :menu="menuItems" />

          <nav
            aria-labelledby="mobile-menu-recommended"
            class="mobile-menu__recommended"
            v-if="recommended"
          >
            <h2 id="mobile-menu-recommended">
              {{ texts.recommended }}
            </h2>
            <ul>
              <li v-for="(item, index) in recommended" :key="index">
                <a :href="item.link.url" :target="item.link.target">
                  {{ item.link.title }}
                </a>
              </li>
            </ul>
          </nav>

          <div class="mobile-menu__contact-cta">
            <!-- The number and the contact form are links inside a translated
                 sentence, so the markup comes from the string and the clicks
                 are delegated. -->
            <p ref="contactText" v-html="contactUsString"></p>
          </div>
        </div>

        <div class="mobile-menu__footer" ref="footer" :inert="isSearchActive">
          <MobileCitySelect
            v-if="showCitySelect"
            ref="city"
            @open-panel="onPanelToggle"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
  import { usePhoneNumber } from "Main/composables/usePhoneNumber";
  import { isSwipeDown, isSwipeLeft } from "Utils/swipe";
  import { sprintf } from "sprintf-js";
  import { mapActions, mapState } from "vuex";
  import MobileCitySelect from "./MobileCitySelect.vue";
  import NestedMenu from "./NestedMenu.vue";
  import Search from "./Search.vue";

  /** Whichever sticky bar this page type renders — see _mobile-sticky-bar.scss. */
  const STICKY_BAR = ".js-mobile-sticky-bar";

  export default {
    beforeUnmount() {
      window.removeEventListener("resize", this.setSheetBounds);
      window.visualViewport?.removeEventListener("resize", this.setSheetBounds);
      window.visualViewport?.removeEventListener("scroll", this.setSheetBounds);
      this.$refs.contactText?.removeEventListener(
        "click",
        this.onContactTextClick
      );
      window.removeEventListener(
        "city-storage-updated-event",
        this.maybeUpdatePhoneNumber
      );
    },

    components: {
      MobileCitySelect,
      NestedMenu,
      Search,
    },

    computed: {
      ...mapState("mobileMenu", ["isMenuActive"]),

      // Carries the phone number as a link when there is one to show.
      contactUsString() {
        if (!this.phoneNumber) {
          return this.texts.contactUs;
        }

        const { callUs } = this.texts;
        return sprintf(callUs, this.phoneNumberTel, this.phoneNumber);
      },

      // Either sheet covers the menu list. What else each one covers differs, so
      // the header block and the footer take their own flags in the template.
      isSheetOpen() {
        return this.isPanelOpen || this.isSearchActive;
      },

      showTopBlock() {
        return !this.noSearch || this.showCitySelect;
      },
    },

    data() {
      return {
        isPanelOpen: false,
        isSearchActive: false,
        keepsBodyLocked: false,
        menuItems: window.mobileMenuData,
        noSearch: this.$mountEl.dataset.noSearch || null,
        recommended: JSON.parse(this.$mountEl.dataset.recommended) || null,
        showCitySelect: this.$mountEl.dataset.showCitySelect !== "false",
        swipeFrom: null,
        texts: theme_vars.mobileMenu,
      };
    },

    methods: {
      ...mapActions({
        toggleMenu: "mobileMenu/toggle",
      }),

      // A downward swipe inside the sheet scrolls it, so it only dismisses from
      // the top, where there is nothing left to scroll to, and only when the
      // gesture began inside the sheet. Same rule a native bottom sheet uses.
      // The sheet itself no longer scrolls — the region above the pinned field
      // does — so that is what has to be at its top.
      canDismissCitySheet({ x, y }) {
        const sheet = this.$refs.menu?.querySelector(".mobile-city__panel");

        if (!sheet) {
          return false;
        }

        const scroller =
          sheet.querySelector(
            ".city-panel__current, .city-panel__results-group"
          ) ?? sheet;

        if (scroller.scrollTop > 0) {
          return false;
        }

        const { left, right, top, bottom } = sheet.getBoundingClientRect();

        return x >= left && x <= right && y >= top && y <= bottom;
      },

      lockBody() {
        const body = document.querySelector("body");
        if (this.isMenuActive) {
          body.style.overflow = "hidden";
          return;
        }

        this.$refs.search?.clear();

        // The contact form takes the lock from here and releases it when it
        // slides out, so closing on its behalf must not free the body.
        if (this.keepsBodyLocked) {
          this.keepsBodyLocked = false;
          return;
        }

        setTimeout(() => {
          body.style.overflow = "visible";
        }, 400);
      },

      // Bound imperatively: the paragraph is not interactive, only the links
      // inside it are, and they come from the translated string.
      onContactTextClick(event) {
        const { target } = event;

        if (!(target instanceof Element)) {
          return;
        }

        if (target.closest('a[href^="tel:"]')) {
          this.gtmPhoneCallTrigger(event, "Mobile menu");
          return;
        }

        // main.js opens the slide-in off the class and reports the event, so
        // the drawer only has to get out of its way.
        if (target.closest(".js-toggle-global-contact-form")) {
          this.keepsBodyLocked = true;
          this.toggleMenu();
        }
      },

      // Both sheets fill the same space, so only one is ever up. Each close()
      // reports back through its own event, which clears the matching flag.
      onPanelToggle(isOpen) {
        this.isPanelOpen = isOpen;

        if (isOpen) {
          this.$refs.search?.clear();
          this.setSheetBounds();
        }
      },

      onSearchActive(isActive) {
        this.isSearchActive = isActive;

        if (isActive) {
          this.$refs.city?.close();
        }
      },

      onTouchEnd(event) {
        const from = this.swipeFrom;
        this.swipeFrom = null;

        const [touch] = event.changedTouches;

        if (!(from && touch && this.isMenuActive)) {
          return;
        }

        const to = { time: Date.now(), x: touch.clientX, y: touch.clientY };

        // While a sheet is up, a swipe belongs to it, never to the drawer
        // underneath. The city sheet dismisses back down into its footer; the
        // search results scroll the way a dismiss would, so that sheet keeps no
        // gesture of its own.
        if (this.isPanelOpen) {
          if (isSwipeDown(from, to) && this.canDismissCitySheet(from)) {
            this.$refs.city?.close();
          }

          return;
        }

        if (this.isSearchActive) {
          return;
        }

        if (isSwipeLeft(from, to)) {
          this.toggleMenu();
        }
      },

      onTouchStart(event) {
        // One finger, and not from inside a field: dragging sideways in a text
        // input moves the caret, it does not dismiss the drawer.
        const isSingleTouch = event.touches.length === 1;
        const { target } = event;
        const inField =
          target instanceof Element &&
          target.closest("input, textarea, select");

        if (!isSingleTouch || inField) {
          this.swipeFrom = null;
          return;
        }

        const [touch] = event.touches;
        this.swipeFrom = {
          time: Date.now(),
          x: touch.clientX,
          y: touch.clientY,
        };
      },

      // How much of the foot of the screen the page's sticky bar holds, which
      // the drawer keeps clear. Its height rather than its top edge: the bar is
      // transformed up by the cookie banner's height, so leaving that term to
      // CSS keeps the inset right when the banner goes away, without measuring
      // again.
      // Rounded down off the fractional height, never offsetHeight: that
      // rounds to the nearest whole pixel, and rounding up holds the drawer
      // clear of the bar's top edge, which is the seam the page shows through.
      // Short by a fraction instead, so the drawer runs under the bar's top
      // edge — where its shadow already sits — and the two never part.
      setBarBottom(menu) {
        if (!menu) {
          return;
        }

        const bar = document.querySelector(STICKY_BAR);
        const height = bar ? Math.floor(bar.getBoundingClientRect().height) : 0;

        menu.style.setProperty(
          "--mobile-menu-bar-bottom",
          `calc(${height}px + var(--cookie-consent-height, 0px))`
        );
      },

      // Both sheets start under the header block, whose height shifts with the
      // alert bar and the admin bar. The search results run to the bottom edge,
      // but the city sheet stops above the footer that holds its own trigger,
      // so that bound is measured too.
      setSheetBounds() {
        const { menu, top, footer } = this.$refs;

        if (!menu) {
          return;
        }

        // Ahead of the guard below: the bar is outside the drawer and laid out
        // whether or not it is open, and the drawer's own bottom edge comes
        // from this. The regions measured after the guard are inside it, and a
        // hidden drawer measures them all as zero.
        this.setBarBottom(menu);

        if (!this.isMenuActive) {
          return;
        }

        const edge = top
          ? top.getBoundingClientRect().bottom
          : menu.getBoundingClientRect().top;
        const footerTop = footer
          ? footer.getBoundingClientRect().top
          : window.innerHeight;

        menu.style.setProperty("--mobile-menu-sheet-top", `${edge}px`);
        menu.style.setProperty(
          "--mobile-menu-sheet-bottom",
          `${Math.max(window.innerHeight - footerTop, 0)}px`
        );

        this.setViewportInsets(menu);
      },

      // Where the visible part of the screen is, which the bounds above know
      // nothing about: a phone keyboard leaves the layout viewport alone and
      // takes the foot of the visual one, panning the rest down by offsetTop.
      // The sheets hold their own edges against these, so both read 0 with no
      // keyboard up. Without the API there is nothing to measure and the CSS
      // falls back to the bounds alone.
      setViewportInsets(menu) {
        const viewport = window.visualViewport;

        if (!viewport) {
          return;
        }

        const covered =
          window.innerHeight - (viewport.offsetTop + viewport.height);

        menu.style.setProperty(
          "--mobile-menu-viewport-top",
          `${Math.max(viewport.offsetTop, 0)}px`
        );
        menu.style.setProperty(
          "--mobile-menu-viewport-bottom",
          `${Math.max(covered, 0)}px`
        );
      },
    },

    mounted() {
      // So the drawer already clears the bar the first time it opens, rather
      // than being measured on the way in. The bar the theme renders is here
      // from first paint; the one a page type mounts as its own app is caught
      // by the measurement the open itself triggers.
      this.setSheetBounds();

      window.addEventListener("resize", this.setSheetBounds);

      // Opening the keyboard resizes the visual viewport without resizing the
      // window, and panning it fires scroll rather than resize, so the sheets
      // need both events to stay inside what is on screen.
      window.visualViewport?.addEventListener("resize", this.setSheetBounds);
      window.visualViewport?.addEventListener("scroll", this.setSheetBounds);
      this.$refs.contactText?.addEventListener(
        "click",
        this.onContactTextClick
      );

      if (this.hidePhoneNumber) {
        this.updatePhoneNumber(null);
        return;
      }

      if (!this.isTherapyPage) {
        window.addEventListener(
          "city-storage-updated-event",
          this.maybeUpdatePhoneNumber
        );
        this.maybeUpdatePhoneNumber();
      }
    },
    name: "MobileMenu",

    setup() {
      const {
        phoneNumber,
        phoneNumberTel,
        hidePhoneNumber,
        isTherapyPage,
        countryPhoneCode,
        maybeUpdatePhoneNumber,
        updatePhoneNumber,
        ctmRunNow,
        ctmInit,
        gtmPhoneCallTrigger,
      } = usePhoneNumber();
      return {
        countryPhoneCode,
        ctmInit,
        ctmRunNow,
        gtmPhoneCallTrigger,
        hidePhoneNumber,
        isTherapyPage,
        maybeUpdatePhoneNumber,
        phoneNumber,
        phoneNumberTel,
        updatePhoneNumber,
      };
    },

    watch: {
      isMenuActive(isActive) {
        this.lockBody();

        if (isActive) {
          // Before the render, not after it: the enter transition is `all`, so
          // a bottom edge set once the drawer is on its way in gets animated
          // and the drawer grows into place instead of arriving at its size.
          // A watcher runs ahead of the re-render, and the bar is outside the
          // drawer, so it measures fine while the drawer is still hidden.
          this.setBarBottom(this.$refs.menu);
          this.$nextTick(this.setSheetBounds);
        }
      },
    },
  };
</script>
